import React from "react"
import autobind from "class-autobind"
import { Dialog, FlatButton, Checkbox, RadioButton, RadioButtonGroup, TextField } from "material-ui"
import strings from "../../../localization/strings"
import { IDate, ITimecardAdhocExportRequest, IDateRange, ITimecardDates, IPayrollPayCalendar, EExceptionType, IUserFilter } from "../../../models"
import DateRangeSelect from "../../../components/common/DateRangeSelect"
import * as reportAction from "../../../actions/ReportAction"
import AccountStore from "../../../stores/AccountStore"
import HierarchySettingsStore from "../../../stores/HierarchySettingsStore"
import moment from "moment"
import * as timecardHelper from "../../../helpers/timecardHelper"
import { grey700 } from "material-ui/styles/colors"
import * as payrollHelper from "../../../helpers/payrollHelper"
import { checkPositionFieldAvailable } from "../../../helpers/positionHelper"
import { checkJobFieldAvailable } from "../../../helpers/jobHelper"
import { checkPhaseFieldAvailable } from "../../../helpers/punchHelper"
import { hasPermission } from "../../../helpers/permissionHelper"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import AppStateStore from "../../../stores/AppStateStore"

interface IProps {
    referenceDate: IDate
    selectedPayrollPayCalendar: IPayrollPayCalendar
    userFilter: IUserFilter
    handleClose(): void

    // job->Timecard report
    jobId: string

    // Timecard page report
    filterByDefaultHierarchy: boolean
    filterDirectReportsOnly: boolean
    filterByPunchHierarchy: boolean
    filterByException: boolean
    filterExceptions: EExceptionType[]
    editAdhocReport: ITimecardAdhocExportRequest
    updateTimecardAdhocReport: (adhocTimecardReports) => void
    adhocTimecardReports: ITimecardAdhocExportRequest[]
}
interface IState {
    request: ITimecardAdhocExportRequest
    dateRangeIsValid: boolean
    saveReport: boolean
    reportName: string
    reportNameError: string
}

export default class AdhocReportDialog extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props)
        const timecardDates = timecardHelper.getTimecardDateByPayrollPayCalendar(props.referenceDate, props.selectedPayrollPayCalendar)
        const request: ITimecardAdhocExportRequest = props.editAdhocReport || {} as ITimecardAdhocExportRequest
        request.DateRange = {
            StartDate: this.props.editAdhocReport ? this.props.editAdhocReport.DateRange.StartDate : moment(timecardDates.StartDate).utc(true).format(),
            EndDate: this.props.editAdhocReport ? this.props.editAdhocReport.DateRange.EndDate : moment(timecardDates.EndDate).utc(true).format()
        }
        request.PayGroupIds = payrollHelper.getPayGroupIdsByPayrollPayCalendar(this.props.selectedPayrollPayCalendar)
        this.state = {
            request,
            dateRangeIsValid: true,
            saveReport: this.props.editAdhocReport && this.props.editAdhocReport.ReportName ? true : false,
            reportName: this.props.editAdhocReport ? this.props.editAdhocReport.ReportName : "",
            reportNameError: ""
        }
        autobind(this)
    }


    async handleExport() {
        if (!this.state.dateRangeIsValid) {
            return
        }
        if (this.state.saveReport && (!this.state.reportName || this.state.reportName.trim().length === 0)) {
            this.setState({ reportNameError: strings.This_field_is_required })
            return
        }
        else if (this.state.saveReport && this.state.reportName && this.duplicatedReportName()) {
            this.setState({ reportNameError: strings.Report_with_this_name_already_Exists })
            return
        }
        this.props.handleClose()
        if (this.state.saveReport) {
            AppStateStore.setProgressSnackbarContent("loading")
            if (this.props.editAdhocReport) {
                const res = await reportAction.updateAdhocReport(AccountStore.getCurrentAccount().Id, { ...this.state.request, ReportName: this.state.saveReport ? this.state.reportName.trim() : undefined })
                this.props.updateTimecardAdhocReport(res.data.TimecardAdhocReports)
                AppStateStore.setProgressSnackbarContent(strings.Report_updated_successfully)
            }
            else if (!this.props.editAdhocReport) {
                const res = await reportAction.saveAdhocReport(AccountStore.getCurrentAccount().Id, { ...this.state.request, ReportName: this.state.saveReport ? this.state.reportName.trim() : undefined })
                this.props.updateTimecardAdhocReport(res.data.TimecardAdhocReports)
                AppStateStore.setProgressSnackbarContent(strings.Report_saved_successfully)
            }
        }
        this.props.jobId ?
            reportAction.downloadJobTimecardAdhocReport(
                AccountStore.getCurrentAccount().Id,
                this.props.jobId,
                { ...this.state.request, ReportName: this.state.saveReport ? this.state.reportName.trim() : undefined },
                this.props.userFilter
            ) :
            reportAction.downloadAdhocReport(
                AccountStore.getCurrentAccount().Id,
                HierarchySettingsStore.getGlobalLevelItemFilterId(),
                this.props.filterByDefaultHierarchy,
                this.props.filterDirectReportsOnly,
                this.props.filterByPunchHierarchy,
                this.props.filterByException,
                this.props.filterExceptions,
                { ...this.state.request, ReportName: this.state.saveReport ? this.state.reportName.trim() : undefined },
                this.props.userFilter
            )
    }

    handleChange(e, checked: boolean) {
        const { request } = this.state
        request[e.target.name] = checked
        this.setState({ request })
    }

    toggleSaveReport(_, saveReport: boolean) {
        this.setState({ saveReport })
    }

    async handleDelete() {
        this.props.handleClose()
        AppStateStore.setProgressSnackbarContent("loading")
        const res = await reportAction.deleteAdhocReport(AccountStore.getCurrentAccount().Id, this.state.request.ReportId)
        this.props.updateTimecardAdhocReport(res.data.TimecardAdhocReports)
        AppStateStore.setProgressSnackbarContent(strings.Report_deleted_successfully)
    }

    duplicatedReportName() {
        if (this.state.saveReport && this.state.reportName && this.props.adhocTimecardReports) {
            if (this.props.editAdhocReport) {
                return this.props.adhocTimecardReports.filter((report) => report.ReportId !== this.state.request.ReportId).some(
                    (item) => item.ReportName.toLowerCase().trim() === this.state.reportName.toLowerCase().trim(),
                )
            } else {
                return this.props.adhocTimecardReports.some(
                    (item) => item.ReportName.toLowerCase().trim() === this.state.reportName.toLowerCase().trim(),
                )
            }
        }
    }


    render() {
        const request = this.state.request
        const customFieldLabels = AccountStore.getCurrentAccountSettings() && AccountStore.getCurrentAccountSettings().CustomFieldSettings.EmployeeCustomLabels
        return (
            <Dialog
                open
                autoScrollBodyContent
                title={strings.Adhoc_Report}
                actions={
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                            {this.props.editAdhocReport && <FlatButton data-intercom-target={"Timecards-Reports-Btn-DeleteAdhocReport"} key="delete" label={strings.Delete} secondary onTouchTap={this.handleDelete} />}
                        </div>
                        <div>
                            <FlatButton data-intercom-target={"Timecards-Reports-Btn-CloseAdhocReportDialog"} key="cancel" label={strings.CANCEL} secondary onTouchTap={this.props.handleClose} />
                            <FlatButton data-intercom-target={"Timecards-Reports-Btn-ExportAdhocReport"} key="export" label={strings.Export} primary onTouchTap={this.handleExport} />
                        </div>
                    </div>

                }
                contentStyle={{ minWidth: 800 }}
            >
                <div style={{ textAlign: "center", marginBottom: 15, color: grey700 }}>{strings.Select_the_data_you_want_to_export_as_unique_columns_in_your_report}</div>
                <div style={{ marginBottom: 10, display: "flex", marginTop: 10 }}>

                    <Checkbox
                        label={strings.Save_to_Timecard_Reports}

                        onCheck={this.toggleSaveReport}
                        style={{ width: "50%", marginTop: "15px" }}
                        checked={this.state.saveReport}
                    />
                    {this.state.saveReport && <TextField value={this.state.reportName} onChange={(e) => this.setState({ reportName: e.target.value })} style={{ width: "100%", marginTop: "-25px" }}
                        errorStyle={{ bottom: 15, fontSize: 11 }}
                        errorText={this.state.reportNameError}
                        floatingLabelText={strings.report_Name}
                    />}
                </div>
                <div style={{ display: "flex" }}>
                    <div style={{ width: 235, marginRight: 15 }}>
                        <div style={{ color: grey700, marginBottom: 5 }}>{strings.Employee_Data}</div>
                        <div style={{ border: "1px solid", borderColor: grey700, padding: 10 }}>
                            <Checkbox
                                name="Name"
                                label={strings.Name}
                                style={{ marginBottom: 10 }}
                                onCheck={this.handleChange}
                                checked={request.Name}
                            />
                            <Checkbox
                                name="Id"
                                label={strings.Id}
                                style={{ marginBottom: 10 }}
                                onCheck={this.handleChange}
                                checked={request.Id}
                            />
                            <Checkbox
                                name="DefaultHierarchy"
                                label={strings.Default_Hierarchy}
                                style={{ marginBottom: 10 }}
                                onCheck={this.handleChange}
                                checked={request.DefaultHierarchy}
                            />
                            {AccountStore.getJobsEnabled() &&
                                <Checkbox
                                    name="DefaultJob"
                                    label={`${strings.Default} ${checkJobFieldAvailable('singular') ? AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel : strings.Job}`}
                                    style={{ marginBottom: 10 }}
                                    onCheck={this.handleChange}
                                    checked={request.DefaultJob}
                                />
                            }
                            {AccountStore.getPayrollEnabled() &&
                                <Checkbox
                                    name="PayrollHierarchy"
                                    label={strings.Payroll_Hierarchy}
                                    style={{ marginBottom: 10 }}
                                    onCheck={this.handleChange}
                                    checked={request.PayrollHierarchy}
                                />
                            }
                            <Checkbox
                                name="Field1"
                                label={customFieldLabels.Field1Label ? customFieldLabels.Field1Label : `${strings.Custom_Field} 1`}
                                style={{ marginBottom: 10 }}
                                onCheck={this.handleChange}
                                checked={request.Field1}
                            />
                            <Checkbox
                                name="Field2"
                                label={customFieldLabels.Field2Label ? customFieldLabels.Field2Label : `${strings.Custom_Field} 2`}
                                style={{ marginBottom: 10 }}
                                onCheck={this.handleChange}
                                checked={request.Field2}
                            />
                            <Checkbox
                                name="Field3"
                                label={customFieldLabels.Field3Label ? customFieldLabels.Field3Label : `${strings.Custom_Field} 3`}
                                style={{ marginBottom: 10 }}
                                onCheck={this.handleChange}
                                checked={request.Field3}
                            />
                            <Checkbox
                                name="Field4"
                                label={customFieldLabels.Field4Label ? customFieldLabels.Field4Label : `${strings.Custom_Field} 4`}
                                style={{ marginBottom: 10 }}
                                onCheck={this.handleChange}
                                checked={request.Field4}
                            />
                            <Checkbox
                                name="HireDate"
                                label={strings.Hire_Date}
                                onCheck={this.handleChange}
                                checked={request.HireDate}
                            />
                        </div>
                    </div>
                    <div style={{ width: 500 }}>
                        <div style={{ color: grey700, marginBottom: 5 }}>{strings.Timeframe}</div>
                        <div style={{ border: "1px solid", borderColor: grey700, padding: 10, display: "flex" }}>
                            <AdhocReportDateRange
                                dateRange={this.state.request.DateRange}
                                selectedPayrollPayCalendar={this.props.selectedPayrollPayCalendar}
                                handleChange={(dateRange, isValid) => {
                                    const { request } = this.state
                                    request.DateRange = dateRange
                                    this.setState({ request, dateRangeIsValid: isValid })
                                }}
                            />
                        </div>
                        <div style={{ marginTop: 20 }}>
                            <div style={{ color: grey700, marginBottom: 5 }}>{strings.Group_PunchSet_Data_By}</div>
                            <div style={{ display: "flex", border: "1px solid", borderColor: grey700, padding: 10 }}>
                                <div style={{ flex: 1 }}>
                                    <Checkbox
                                        name="HidePunchSet"
                                        label={strings.Hide_PunchSet}
                                        style={{ marginBottom: 10 }}
                                        onCheck={this.handleChange}
                                        checked={request.HidePunchSet}
                                    />
                                    <Checkbox
                                        name="ShowGroupByDay"
                                        label={strings.Show_Day_Data}
                                        style={{ marginBottom: 10 }}
                                        onCheck={this.handleChange}
                                        checked={request.ShowGroupByDay}
                                    />
                                    <Checkbox
                                        name="ShowGroupByWeek"
                                        label={strings.Show_Week_Data}
                                        style={{ marginBottom: 10 }}
                                        onCheck={this.handleChange}
                                        checked={request.ShowGroupByWeek}
                                    />
                                    <Checkbox
                                        name="ShowGroupByEmployee"
                                        label={strings.Show_Employee_Data}
                                        style={{ marginBottom: 10 }}
                                        onCheck={this.handleChange}
                                        checked={request.ShowGroupByEmployee}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div style={{ marginTop: 20 }}>
                    <div style={{ color: grey700, marginBottom: 5 }}>{strings.PunchSet_Data}</div>
                    <div style={{ display: "flex", border: "1px solid", borderColor: grey700, padding: 10 }}>
                        <div style={{ flex: 1 }}>
                            <Checkbox
                                name="StartTime"
                                label={strings.Start_Time}
                                style={{ marginBottom: 10 }}
                                onCheck={this.handleChange}
                                checked={request.StartTime}
                            />
                            <Checkbox
                                name="EndTime"
                                label={strings.End_Time}
                                style={{ marginBottom: 10 }}
                                onCheck={this.handleChange}
                                checked={request.EndTime}
                            />
                            <Checkbox
                                name="Hierarchy"
                                label={strings.Hierarchy}
                                style={{ marginBottom: 10 }}
                                onCheck={this.handleChange}
                                checked={request.Hierarchy}
                            />
                            {AccountStore.getJobsEnabled() &&
                                <Checkbox
                                    name="Job"
                                    label={checkJobFieldAvailable('singular') ? `${AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel}` : strings.Job}
                                    style={{ marginBottom: 10 }}
                                    onCheck={this.handleChange}
                                    checked={request.Job}
                                />
                            }
                            {AccountStore.getPhasesEnabled() &&
                                <Checkbox
                                    name="Phase"
                                    label={checkPhaseFieldAvailable("singular") ? `${AccountStore.getCurrentAccountSettings().CustomFieldSettings.PhaseFieldLabels.SingularPhaseLabel}` : strings.Phase}
                                    style={{ marginBottom: 10 }}
                                    onCheck={this.handleChange}
                                    checked={request.Phase}
                                />
                            }
                            <Checkbox
                                name="Position"
                                label={checkPositionFieldAvailable('singular')
                                    ?
                                    AccountStore.getCurrentAccountSettings().CustomFieldSettings.PositionFieldLabels.SingularPositionLabel
                                    : strings.Position}
                                onCheck={this.handleChange}
                                checked={request.Position}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <Checkbox
                                name="RegHours"
                                label={strings.Reg_Hours}
                                style={{ marginBottom: 10 }}
                                onCheck={this.handleChange}
                                checked={request.RegHours}
                            />
                            <Checkbox
                                name="OTHours"
                                label={strings.OT_Hours}
                                style={{ marginBottom: 10 }}
                                onCheck={this.handleChange}
                                checked={request.OTHours}
                            />
                            {!AccountStore.getHideOT2() &&
                                <Checkbox
                                    name="OT2Hours"
                                    label={strings.OT2_Hours}
                                    style={{ marginBottom: 10 }}
                                    onCheck={this.handleChange}
                                    checked={request.OT2Hours}
                                />
                            }
                            <Checkbox
                                name="TotalHours"
                                label={strings.TotalHours}
                                style={{ marginBottom: 10 }}
                                onCheck={this.handleChange}
                                checked={request.TotalHours}
                            />
                            <Checkbox
                                name="PayType"
                                label={strings.Pay_Type}
                                onCheck={this.handleChange}
                                checked={request.PayType}
                            />
                        </div>
                        {hasPermission(AuthenticationStore.Permissions.Account.ViewRates) &&
                            <div style={{ flex: 1 }}>
                                {!AccountStore.getHideBilling() &&
                                    <Checkbox
                                        name="BillRate"
                                        label={strings.Bill_Rate_$}
                                        style={{ marginBottom: 10 }}
                                        onCheck={this.handleChange}
                                        checked={request.BillRate}
                                    />
                                }
                                <Checkbox
                                    name="PayRate"
                                    label={strings.Pay_Rate_$}
                                    style={{ marginBottom: 10 }}
                                    onCheck={this.handleChange}
                                    checked={request.PayRate}
                                />
                                <Checkbox
                                    name="RegTotal"
                                    label={strings.Reg_Total_$}
                                    style={{ marginBottom: 10 }}
                                    onCheck={this.handleChange}
                                    checked={request.RegTotal}
                                />
                                <Checkbox
                                    name="OTTotal"
                                    label={strings.OT_Total_$}
                                    style={{ marginBottom: 10 }}
                                    onCheck={this.handleChange}
                                    checked={request.OTTotal}
                                />
                                {!AccountStore.getHideOT2() &&
                                    <Checkbox
                                        name="OT2Total"
                                        label={strings.OT2_Total_$}
                                        style={{ marginBottom: 10 }}
                                        onCheck={this.handleChange}
                                        checked={request.OT2Total}
                                    />
                                }
                                <Checkbox
                                    name="Total"
                                    label={strings.Total_$}
                                    onCheck={this.handleChange}
                                    checked={request.Total}
                                />
                            </div>}
                        {AccountStore.getExpensesEnabled() &&
                            <div style={{ flex: 1 }}>
                                <Checkbox
                                    name="TimeExpense"
                                    label={strings.Time_Expenses}
                                    style={{ marginBottom: 10 }}
                                    onCheck={this.handleChange}
                                    checked={request.TimeExpense}
                                />
                                <Checkbox
                                    name="AmountExpense"
                                    label={strings.Monetary_Expenses}
                                    style={{ marginBottom: 10 }}
                                    onCheck={this.handleChange}
                                    checked={request.AmountExpense}
                                />
                            </div>}
                    </div>
                </div>
            </Dialog>
        )
    }
}

interface IAdhocReportDateRangeProps {
    dateRange: IDateRange
    selectedPayrollPayCalendar: IPayrollPayCalendar
    handleChange(dateRange: IDateRange, isValid: boolean): void
}
interface IAdhocReportDateRangeState {
    selectedType: "ThisWeek" | "LastWeek" | "ThisPayperiod" | "LastPayperiod" | "Custom"
}
export class AdhocReportDateRange extends React.Component<IAdhocReportDateRangeProps, IAdhocReportDateRangeState> {
    private ThisWeekDates: ITimecardDates; LastWeekDates: ITimecardDates; ThisLastPayperiod
    constructor(props: IAdhocReportDateRangeProps) {
        super(props)
        this.ThisWeekDates = timecardHelper.getTimecardDateByPayrollPayCalendar(moment(), this.props.selectedPayrollPayCalendar)
        this.ThisWeekDates.StartDate = moment(this.ThisWeekDates.StartDate).utc(true).format()
        this.ThisWeekDates.EndDate = moment(this.ThisWeekDates.EndDate).utc(true).format()
        this.LastWeekDates = timecardHelper.getTimecardDateByPayrollPayCalendar(moment().add(-1, "week"), this.props.selectedPayrollPayCalendar)
        this.LastWeekDates.StartDate = moment(this.LastWeekDates.StartDate).utc(true).format()
        this.LastWeekDates.EndDate = moment(this.LastWeekDates.EndDate).utc(true).format()
        this.ThisLastPayperiod = payrollHelper.getThisLastPayrollPeriodByPayrollPayCalendar(this.props.selectedPayrollPayCalendar)
        this.ThisLastPayperiod.thisPayrollPeriod.StartDate = moment(this.ThisLastPayperiod.thisPayrollPeriod.StartDate).utc(true).format()
        this.ThisLastPayperiod.thisPayrollPeriod.EndDate = moment(this.ThisLastPayperiod.thisPayrollPeriod.EndDate).utc(true).format()
        this.ThisLastPayperiod.lastPayrollPeriod.StartDate = moment(this.ThisLastPayperiod.lastPayrollPeriod.StartDate).utc(true).format()
        this.ThisLastPayperiod.lastPayrollPeriod.EndDate = moment(this.ThisLastPayperiod.lastPayrollPeriod.EndDate).utc(true).format()
        let selectedType = null

        selectedType = "Custom"
        if (moment(this.ThisWeekDates.StartDate).isSame(props.dateRange.StartDate) && moment(this.ThisWeekDates.EndDate).isSame(props.dateRange.EndDate))
            selectedType = "ThisWeek"
        if (moment(this.LastWeekDates.StartDate).isSame(props.dateRange.StartDate) && moment(this.LastWeekDates.EndDate).isSame(props.dateRange.EndDate))
            selectedType = "LastWeek"
        this.state = {
            selectedType
        }
        autobind(this)
    }

    handleChange(e, value) {
        this.setState({ selectedType: value })
        switch (value) {
            case "ThisWeek":
                this.props.handleChange({ StartDate: this.ThisWeekDates.StartDate, EndDate: this.ThisWeekDates.EndDate }, true)
                break
            case "LastWeek":
                this.props.handleChange({ StartDate: this.LastWeekDates.StartDate, EndDate: this.LastWeekDates.EndDate }, true)
                break
            case "ThisPayperiod":
                this.props.handleChange({ StartDate: this.ThisLastPayperiod.thisPayrollPeriod.StartDate, EndDate: this.ThisLastPayperiod.thisPayrollPeriod.EndDate }, true)
                break
            case "LastPayperiod":
                this.props.handleChange({ StartDate: this.ThisLastPayperiod.lastPayrollPeriod.StartDate, EndDate: this.ThisLastPayperiod.lastPayrollPeriod.EndDate }, true)
                break
        }
    }

    render() {
        return (
            <div style={{ display: "flex" }}>
                <div style={{ width: 205 }}>
                    <RadioButtonGroup
                        name="DefaultRange"
                        valueSelected={this.state.selectedType}
                        onChange={this.handleChange}
                    >
                        <RadioButton
                            value="ThisWeek"
                            label={strings.This_Week}
                            style={{ marginBottom: 9 }}
                        />
                        <RadioButton
                            value="LastWeek"
                            label={strings.Last_Week}
                            style={{ marginBottom: 9 }}
                        />
                        <RadioButton
                            value="ThisPayperiod"
                            label={strings.This_Pay_Period}
                            style={{ marginBottom: 9 }}
                        />
                        <RadioButton
                            value="LastPayperiod"
                            label={strings.Last_Pay_Period}
                        />
                    </RadioButtonGroup>
                </div>
                <div>
                    <RadioButtonGroup
                        name="CustomRange"
                        valueSelected={this.state.selectedType}
                        onChange={this.handleChange}
                    >
                        <RadioButton
                            value="Custom"
                            label={strings.Custom_Range}
                        />
                    </RadioButtonGroup>
                    <div style={{ marginTop: -10 }}>
                        <DateRangeSelect
                            sameDatesAllowed
                            readonly={this.state.selectedType !== "Custom"}
                            startDate={this.props.dateRange.StartDate}
                            endDate={this.props.dateRange.EndDate}
                            datePickerWidth={120}
                            handleChange={(startDate, endDate, isValid) =>
                                this.props.handleChange({ StartDate: startDate as string, EndDate: endDate as string }, isValid)
                            }
                        />
                    </div>
                </div>
            </div>
        )
    }
}